<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import type { TReservationStatus } from '@/interfaces/IReservation';
  const props = withDefaults(
    defineProps<{
      status: TReservationStatus;
      lateMinutes?: number;
      /** Closed by the system without staff marking the arrival. */
      autoClosed?: boolean;
    }>(),
    { lateMinutes: 0, autoClosed: false }
  );
  interface IBadgeRole {
    color: 'neutral' | 'info' | 'success' | 'error' | 'warning';
    variant: 'soft' | 'outline';
    icon?: string;
  }
  const roles: Record<TReservationStatus, IBadgeRole> = {
    pending: { color: 'error', variant: 'soft' },
    pending_payment: {
      color: 'warning',
      variant: 'outline',
      icon: 'credit-card-clock-outline',
    },
    confirmed: { color: 'info', variant: 'soft' },
    arrived: { color: 'warning', variant: 'soft', icon: 'walk' },
    seated: {
      color: 'success',
      variant: 'soft',
      icon: 'silverware-fork-knife',
    },
    finished: { color: 'neutral', variant: 'soft' },
    cancelled: { color: 'neutral', variant: 'outline' },
    no_show: { color: 'error', variant: 'soft', icon: 'account-off-outline' },
  };
  const late = computed(
    () =>
      (props.status === 'confirmed' || props.status === 'arrived') &&
      props.lateMinutes > 0
  );
  const unreviewed = computed(
    () => props.status === 'no_show' && props.autoClosed
  );
  const role = computed<IBadgeRole>(() =>
    late.value
      ? { color: 'error', variant: 'outline', icon: 'clock-alert-outline' }
      : unreviewed.value
      ? { color: 'neutral', variant: 'outline', icon: 'account-question-outline' }
      : roles[props.status]
  );
  const labelKey = computed(() =>
    unreviewed.value
      ? 'reservations.status.no_show_unreviewed'
      : `reservations.status.${props.status}`
  );
</script>

<template>
  <PosUiBadge v-bind="role" class="reservation-status" :class="status">
    <span v-if="status === 'pending'" class="pulse-dot" aria-hidden="true" />
    {{
      late
        ? $t('reservations.status.late', { minutes: lateMinutes })
        : $t(labelKey)
    }}
  </PosUiBadge>
</template>

<style scoped lang="scss">
  .reservation-status {
    &.pending_payment {
      @apply tw-border-dashed;
    }
    .pulse-dot {
      // Margin, not the badge's flex gap (old POS hardware, Chrome < 84).
      @apply tw-mr-1 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-red-500;
      animation: reservation-pulse 2s ease-in-out infinite;
    }
  }
  @keyframes reservation-pulse {
    50% {
      opacity: 0.4;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .reservation-status .pulse-dot {
      animation: none;
    }
  }
</style>
